import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  Textarea,
  Button,
  Flex,
  Badge,
  FormControl,
  FormLabel,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Input,
  SimpleGrid,
  useDisclosure,
  IconButton,
  FormHelperText,
  Spinner,
  VStack,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { AddIcon, CloseIcon, ArrowForwardIcon } from "@chakra-ui/icons";
import Card from "./Card/Card";
import CardBody from "./Card/CardBody";
import CardHeader from "./Card/CardHeader";
import { useAuth } from "../context/AuthContext";
import { useProject } from "../context/ProjectContext";
import { useTranslation } from "react-i18next";
import type { ProductBrief } from "../types/productBrief";
import { createEmptyProductBrief } from "../types/productBrief";
import { customDescriptionToProductBrief } from "../utils/customDescriptionToProductBrief";
import { productBriefToPrompt } from "../utils/productBriefToPrompt";

interface ProductBriefCreatorProps {
  projectId: string | undefined;
}

const ProductBriefCreator: React.FC<ProductBriefCreatorProps> = ({ projectId }) => {
  const { currentUser, currentLanguage, getToken } = useAuth();
  const { project, productBrief: ctxProductBrief, setProductBrief } = useProject();
  const { t } = useTranslation();
  const toast = useToast();

  const [productBrief, setProductBriefLocal] = useState<ProductBrief>(createEmptyProductBrief());
  const [questions, setQuestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [answer, setAnswer] = useState("");
  const [isModified, setIsModified] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();

  // New item inputs (for add buttons)
  const [newSellingPoint, setNewSellingPoint] = useState("");
  const [newPainPointProblem, setNewPainPointProblem] = useState("");
  const [newPainPointSolution, setNewPainPointSolution] = useState("");
  const [newFeature, setNewFeature] = useState("");

  useEffect(() => {
    const pb = ctxProductBrief ?? project?.productBrief;
    if (pb && typeof pb === "object") {
      setProductBriefLocal({
        baseInfo: pb.baseInfo || { productName: "", brand: "" },
        sellingPoints: Array.isArray(pb.sellingPoints) ? pb.sellingPoints : [],
        painPoints: Array.isArray(pb.painPoints) ? pb.painPoints : [],
        features: Array.isArray(pb.features) ? pb.features : [],
        freeformNotes: pb.freeformNotes,
        qaPairs: pb.qaPairs,
        assertions: pb.assertions,
      } as ProductBrief);
    } else {
      const legacy = project?.customDescription ?? project?.context?.description ?? "";
      const migrated = customDescriptionToProductBrief(legacy);
      if (migrated) {
        setProductBriefLocal(migrated);
      } else {
        const title = project?.name ?? project?.data?.title ?? "";
        const brand = project?.context?.brand ?? project?.brand ?? project?.data?.brand ?? "";
        setProductBriefLocal({
          ...createEmptyProductBrief(),
          baseInfo: { productName: title, brand: brand || "" },
        });
      }
    }
    setQuestions(project?.questions || []);
  }, [project?.id, project?.productBrief, project?.customDescription, project?.questions, ctxProductBrief]);

  const hasContent = () => {
    const { baseInfo, sellingPoints, painPoints, features, freeformNotes } = productBrief;
    return (
      (baseInfo?.productName && baseInfo.productName.trim()) ||
      (baseInfo?.brand && baseInfo.brand.trim()) ||
      sellingPoints.length > 0 ||
      painPoints.length > 0 ||
      features.length > 0 ||
      (freeformNotes && freeformNotes.trim())
    );
  };

  const handleSave = async (pbToSave?: ProductBrief) => {
    setLoading(true);
    const briefToUse = pbToSave || productBrief;
    try {
      const token = await getToken();
      const response = await fetch("/.netlify/functions/saveProductBrief", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          projectId,
          userId: currentUser?.uid,
          productBrief: briefToUse,
          skipQuestionGeneration: true,
          currentLanguage,
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || t("saveDescriptionError"));
      }

      setProductBrief?.(briefToUse);
      setQuestions(project?.questions || []);
      setIsModified(false);
      toast({ title: t("productBriefSaved"), status: "success", duration: 3000, isClosable: true });

      pollForQuestions();
    } catch (error) {
      console.error("Error saving product brief:", error);
      toast({
        title: t("error"),
        description: error instanceof Error ? error.message : t("saveDescriptionError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const pollForQuestions = () => {
    let intervalId: ReturnType<typeof setInterval>;
    intervalId = setInterval(async () => {
      try {
        const token = await getToken();
        const response = await fetch("/.netlify/functions/getProject", {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({ userId: currentUser?.uid, projectId }),
        });
        const result = await response.json();
        if (response.ok && result.project?.questions?.length > 0) {
          setQuestions(result.project.questions);
          clearInterval(intervalId);
        }
      } catch {
        clearInterval(intervalId);
      }
    }, 3500);
    setTimeout(() => clearInterval(intervalId), 15000);
  };

  const handleQuestionClick = (q: string) => {
    setSelectedQuestion(q);
    setAnswer("");
    onOpen();
  };

  const handleSaveAnswer = async () => {
    if (!selectedQuestion || !answer.trim()) return;
    setLoading(true);
    try {
      const token = await getToken();
      const description = productBriefToPrompt(productBrief, t);
      const response = await fetch("/.netlify/functions/updateDescriptionWithAnswer", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          projectId,
          userId: currentUser?.uid,
          description,
          question: selectedQuestion,
          answer: answer.trim(),
        }),
      });

      if (!response.ok) throw new Error(t("updateDescriptionError"));

      const qaPairs = [...(productBrief.qaPairs || []), { question: selectedQuestion, answer: answer.trim() }];
      setProductBriefLocal((prev) => ({ ...prev, qaPairs }));
      setIsModified(true);
      toast({ title: t("answerSaved"), description: t("answerSavedMessage"), status: "success", duration: 3000 });
      pollForQuestions();
    } catch (error) {
      toast({ title: t("error"), description: t("updateDescriptionError"), status: "error" });
    } finally {
      setLoading(false);
    }
    onClose();
    setSelectedQuestion(null);
    setAnswer("");
  };

  const addSellingPoint = () => {
    if (!newSellingPoint.trim()) return;
    const updated = {
      ...productBrief,
      sellingPoints: [...productBrief.sellingPoints, newSellingPoint.trim()],
    };
    setProductBriefLocal(updated);
    setNewSellingPoint("");
    handleSave(updated);
  };

  const addPainPoint = () => {
    if (!newPainPointProblem.trim()) return;
    const updated = {
      ...productBrief,
      painPoints: [...productBrief.painPoints, { problem: newPainPointProblem.trim(), solution: newPainPointSolution.trim() || undefined }],
    };
    setProductBriefLocal(updated);
    setNewPainPointProblem("");
    setNewPainPointSolution("");
    handleSave(updated);
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    const updated = {
      ...productBrief,
      features: [...productBrief.features, newFeature.trim()],
    };
    setProductBriefLocal(updated);
    setNewFeature("");
    handleSave(updated);
  };

  const removeSellingPoint = (i: number) => {
    const updated = {
      ...productBrief,
      sellingPoints: productBrief.sellingPoints.filter((_, idx) => idx !== i),
    };
    setProductBriefLocal(updated);
    handleSave(updated);
  };

  const removePainPoint = (i: number) => {
    const updated = {
      ...productBrief,
      painPoints: productBrief.painPoints.filter((_, idx) => idx !== i),
    };
    setProductBriefLocal(updated);
    handleSave(updated);
  };

  const removeFeature = (i: number) => {
    const updated = {
      ...productBrief,
      features: productBrief.features.filter((_, idx) => idx !== i),
    };
    setProductBriefLocal(updated);
    handleSave(updated);
  };

  return (
    <Card p="1rem" style={{ marginTop: "30px" }}>
      <CardHeader p="5px" mb="12px" style={{ width: "100%" }}>
        <Flex justify="space-between" align="center">
          <Text fontSize="lg" color="white" fontWeight="bold">
            {t("productBrief")}
          </Text>
        </Flex>
      </CardHeader>
      <CardBody p="5px">
        <Box position="relative" opacity={loading ? 0.7 : 1}>
          <SimpleGrid width="100%" columns={1} spacing={6}>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>{t("sellingPoints")}</FormLabel>
                <HStack mb={2}>
                  <Input
                    placeholder={t("addSellingPoint")}
                    value={newSellingPoint}
                    onChange={(e) => setNewSellingPoint(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSellingPoint())}
                    size="sm"
                  />
                  <IconButton aria-label={t("add")} icon={<AddIcon />} onClick={addSellingPoint} size="sm" colorScheme="teal" />
                </HStack>
                <Flex wrap="wrap" gap={2}>
                  {productBrief.sellingPoints.map((sp, i) => (
                    <Badge key={i} colorScheme="teal" p={2} display="flex" alignItems="center" gap={2}>
                      {sp}
                      <CloseIcon w={3} cursor="pointer" onClick={() => removeSellingPoint(i)} />
                    </Badge>
                  ))}
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel>{t("painPoints")}</FormLabel>
                <VStack align="stretch" spacing={2} mb={2}>
                  <Input
                    placeholder={t("problem")}
                    value={newPainPointProblem}
                    onChange={(e) => setNewPainPointProblem(e.target.value)}
                    size="sm"
                  />
                  <Input
                    placeholder={t("solution")}
                    value={newPainPointSolution}
                    onChange={(e) => setNewPainPointSolution(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPainPoint())}
                    size="sm"
                  />
                  <Button size="sm" leftIcon={<AddIcon />} onClick={addPainPoint} colorScheme="teal" isDisabled={!newPainPointProblem.trim()}>
                    {t("addPainPoint")}
                  </Button>
                </VStack>
                <VStack align="stretch" spacing={2}>
                  {productBrief.painPoints.map((pp, i) => (
                    <Flex key={i} align="center" gap={2} fontSize="sm" wrap="wrap">
                      <Badge colorScheme="red" p={2}>{pp.problem}</Badge>
                      {pp.solution && (
                        <>
                          <Icon as={ArrowForwardIcon} color="gray.400" />
                          <Badge colorScheme="green" p={2}>{pp.solution}</Badge>
                        </>
                      )}
                      <IconButton 
                        aria-label={t("delete")} 
                        icon={<CloseIcon />} 
                        size="xs" 
                        variant="ghost" 
                        onClick={() => removePainPoint(i)} 
                        ml="auto"
                      />
                    </Flex>
                  ))}
                </VStack>
              </FormControl>

              <FormControl>
                <FormLabel>{t("features")}</FormLabel>
                <HStack mb={2}>
                  <Input
                    placeholder={t("addFeature")}
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())}
                    size="sm"
                  />
                  <IconButton aria-label={t("add")} icon={<AddIcon />} onClick={addFeature} size="sm" colorScheme="teal" />
                </HStack>
                <Flex wrap="wrap" gap={2}>
                  {productBrief.features.map((f, i) => (
                    <Badge key={i} colorScheme="blue" p={2} display="flex" alignItems="center" gap={2}>
                      {f}
                      <CloseIcon w={3} cursor="pointer" onClick={() => removeFeature(i)} />
                    </Badge>
                  ))}
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel>{t("freeformNotes")}</FormLabel>
                <Textarea
                  value={productBrief.freeformNotes || ""}
                  onChange={(e) => {
                    setProductBriefLocal((prev) => ({ ...prev, freeformNotes: e.target.value }));
                    setIsModified(true);
                  }}
                  placeholder={t("describeYourProductPlaceholder")}
                  rows={4}
                  size="sm"
                />
              </FormControl>
            </VStack>

            <VStack align="stretch" spacing={4}>
              {questions.length > 0 && (
                <FormControl>
                  <Text mb={2}>{t("refineFurther")}</Text>
                  <FormHelperText mb={2}>{t("clickQuestionToExpand")}</FormHelperText>
                  <Flex wrap="wrap" gap={2}>
                    {questions.map((q, i) => (
                      <Badge
                        key={i}
                        colorScheme="teal"
                        p={2}
                        cursor="pointer"
                        onClick={() => handleQuestionClick(q)}
                        _hover={{ opacity: 0.8 }}
                      >
                        {q}
                      </Badge>
                    ))}
                  </Flex>
                </FormControl>
              )}
            </VStack>
          </SimpleGrid>

          <Button
            colorScheme="teal"
            onClick={() => handleSave()}
            isLoading={loading}
            isDisabled={loading || !hasContent()}
            mt={4}
          >
            {t("saveDescription")}
          </Button>
          {isModified && (
            <Text color="orange.400" mt={2} fontSize="sm">
              {t("dontForgetToSave")}
            </Text>
          )}

          {loading && (
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              display="flex"
              alignItems="center"
              justifyContent="center"
              bg="blackAlpha.300"
              borderRadius="md"
              zIndex={1}
            >
              <Spinner size="xl" />
            </Box>
          )}
        </Box>

        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t("specify")}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Text mb={3}>{selectedQuestion}</Text>
              <Input
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={t("response")}
                onKeyDown={(e) => e.key === "Enter" && handleSaveAnswer()}
                isDisabled={loading}
              />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="teal" onClick={handleSaveAnswer} isLoading={loading}>
                {t("saveResponse")}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </CardBody>
    </Card>
  );
};

export default ProductBriefCreator;
